import { Photo } from "@/types/photos";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

export const downloadPhoto = async (photo: Photo): Promise<void> => {
  if (!photo) {
    toast({
      title: "Error",
      description: "Photo not found.",
      variant: "destructive",
    });
    return;
  }

  try {
    // Show loading toast
    toast({
      title: "Downloading...",
      description: "Preparing your download.",
    });

    // Extract the file path from the URL
    const fileUrl = new URL(photo.imageUrl);
    const filePath = fileUrl.pathname.split('/public/')[1];

    if (!filePath) {
      throw new Error("Invalid file path");
    }

    // Download the file from Supabase storage
    const { data, error } = await supabase.storage
      .from('photos')
      .download(filePath);

    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error("No data received from storage");
    }

    // Create blob URL
    const blob = new Blob([data], { type: 'image/jpeg' });
    const blobUrl = window.URL.createObjectURL(blob);

    // Create an anchor element
    const link = document.createElement('a');
    link.href = blobUrl;

    // Set filename based on customer name and product
    const filename = `${photo.customerName.replace(/\s+/g, '_')}_${photo.productName.replace(/\s+/g, '_')}.jpg`;
    link.download = filename;

    // Append to body
    document.body.appendChild(link);

    // Trigger download
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    // Show success toast
    toast({
      title: "Download Complete",
      description: `${filename} has been downloaded.`,
    });
  } catch (error) {
    console.error("Download failed:", error);
    toast({
      title: "Download Failed",
      description: error instanceof Error ? error.message : "There was an error downloading this photo.",
      variant: "destructive",
    });
  }
};
